import React, { useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';

/**
 * Component for displaying and managing countdown timer during recording initiation.
 * It handles the countdown animation and enables recording after countdown completion.
 */
const CountdownTimer = ({
    status,
    updateRecordingTimeLimit = () => { },
    updateStatus = () => { },
}) => {
    let startCountdownTime = null;
    let startRecordingTime;
    let recordingTime = 0; // track the current recording time in seconds

    const [localState, setLocalState] = useReducer((prevState, newState) => ({ ...prevState, ...newState }), {
        showCountdown: false,
        count: '',
        counterStyle: {},
        showSpinner: false,
    });

    const {
        showCountdown, count, counterStyle, showSpinner,
    } = localState;
    const addMoreBtn = document.querySelector('.user-block'); // Select the button element with class 'user-block' to enable/disable it during the countdown.

    // Hook to handle countdown and recording time tracking
    useEffect(() => {
        let animationFrameId;
        // If status is 'recordingCountdown', start countdown animation
        if (status === 'recordingCountdown') {
            startCountdownTime = null; // Reset countdown start time
            setLocalState({
                showCountdown: true, // Show the countdown UI
                showSpinner: true, // Show spinner while countdown is active
            });
            requestAnimationFrame(recorderCountDown); // Start countdown animation
        }
        // Function to track recording time
        const trackRecordingTime = (time) => {
            if (!startRecordingTime) {
                // If recording has just started, initialize start time and keep tracking
                animationFrameId = requestAnimationFrame(trackRecordingTime);
                startRecordingTime = time;
                return;
            }
            // Calculate recording time in seconds
            recordingTime = Math.round((time - startRecordingTime) / 1000);
            // Update recording time limit externally
            updateRecordingTimeLimit(recordingTime, 300);
            // Keep tracking if status is 'recording' and recording time is less than the limit
            if (status && status === 'recording' && recordingTime < 300) {
                animationFrameId = requestAnimationFrame(trackRecordingTime);
            }
        };
        if (status === 'recording') {
            animationFrameId = requestAnimationFrame(trackRecordingTime); // Start tracking recording time
        }
        // Cleanup function to cancel animation frame on component unmount or status change
        return () => {
            cancelAnimationFrame(animationFrameId);
        };
    }, [startRecordingTime, status]);

    // Function to update countdown display
    const countDown = function (time) {
        const num = Math.ceil((3000 - time) / 1000); // Calculate countdown number
        const remainder = time % 1000; // Calculate remainder for animation effects
        let opacity;

        // Calculate opacity based on remainder
        if (remainder < 500) {
            opacity = remainder / 500;
        } else {
            opacity = remainder < 800 ? 1 : ((1000 - remainder) / 200);
        }
        // Style object for countdown display
        const styleVal = {
            transform: 'scale(' + (1 + (1000 - remainder) * 0.3 / 1000) + ')',
            opacity: opacity,
            fontSize: '14em',
        };
        // Update local state with countdown details
        setLocalState({
            showSpinner: false,
            count: num,
            counterStyle: styleVal,
        });
    };

    // Function to handle end of countdown
    const endCountdown = function () {
        // Enable user interactions
        addMoreBtn.disabled = false;
        setTimeout(() => {
            updateStatus('recording'); // Update status to 'recording' after a delay to ensure a smoother transition in the user interface.
        }, 250);
    };

    // Function to handle recorder countdown
    const recorderCountDown = (time) => {
        // Disable user interactions
        addMoreBtn.disabled = true;

        if (!startCountdownTime) {
            // If startCountdownTime is not initialized, meaning this is the first frame of the countdown
            requestAnimationFrame(recorderCountDown); // Request the next frame of the countdown animation
            startCountdownTime = time; // Record the start time of the countdown
            return; // Exit the function to avoid further execution
        }
        // Calculate the total time elapsed since the start of the countdown by subtracting the start time of the countdown from the current time.
        const totalTime = time - startCountdownTime;
        // Continue countdown animation until 3 seconds (3000 milliseconds)
        if (totalTime < 3000) {
            requestAnimationFrame(recorderCountDown);
            countDown(totalTime); // Update countdown display
        } else {
            endCountdown(); // End countdown and enable user interactions
        }
    };

    // Render the CountdownTimer only if status is 'recordingCountdown'
    return status === 'recordingCountdown' ? (
        <div className={`recorder-overlay countdown ${showCountdown ? 'fadeIn' : 'fade-long fadeOut'}`}>
            {/* Render countdown display */}
            <span className={`count font-blue-48 ${showSpinner ? 'fa fa-spinner fa-spin' : ''}`} style={counterStyle}>
                {count}
            </span>
        </div>
    ) : null;
};

CountdownTimer.propTypes = {
    status: PropTypes.string,
    updateRecordingTimeLimit: PropTypes.func,
    updateStatus: PropTypes.func,
};

export default CountdownTimer;
